// base
import { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

// modules
import { CurrentMenuAtom, MenuItemProps, MenuListAtom, type MenuListProps } from 'modules';
import { UserRolesAtom } from 'modules/user/atoms/user.atom';

// libraries
import { compact, flatMap, omit } from 'lodash-es';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

export const useMenuList = () => {
  const userRole = useRecoilValue(UserRolesAtom);

  const [menuList, setMenuList] = useRecoilState(MenuListAtom);
  const setCurrentMenu = useSetRecoilState(CurrentMenuAtom);

  const history = useHistory();
  const pathname = history.location.pathname;

  const menuMap = useMemo(() => new Map(menuList?.map((menu) => [menu.menuId, menu])), [menuList]);

  const getMenuLists = useCallback(async () => {
    if (menuList.length > 0 || !userRole) return;

    try {
      const allMenuList: MenuListProps = await fetch('/assets/menuList-' + userRole + '.json')
        .then((res) => res.json())
        .then((res) => res);
      if (allMenuList) {
        setMenuList(allMenuList.menus);
      }
    } catch (error) {
      console.error('== getMenuLists == : ', error);
    }
  }, [menuList, setMenuList, userRole]);

  const getActiveMenu = useCallback(() => {
    if (!pathname || !menuMap.size) return null;

    const pathSegments = compact(pathname.split('/'));

    const menu = menuMap.get(pathSegments[0]);

    let activeMenu = null;

    if (menu && menu?.subMenus?.length === 0) {
      activeMenu = menu;
    } else {
      activeMenu = menu?.subMenus?.find(
        (menu) => menu.pgmUrl === pathname || menu.pgmUrl === `/${pathname}`
      );
    }

    if (activeMenu) {
      setCurrentMenu(activeMenu);
    }
  }, [pathname, menuMap, setCurrentMenu]);

  const flatMenuList = useMemo(() => {
    function flattenMenus(menus: MenuItemProps[]): MenuItemProps[] {
      return flatMap(menus, (menu) => {
        const menuWithoutSub = omit(menu, 'subMenus');

        if (menu.subMenus && menu.subMenus.length > 0) {
          return [menuWithoutSub, ...flattenMenus(menu.subMenus)];
        }

        return menuWithoutSub;
      });
    }

    const flattenedMenus = flattenMenus(menuList);

    return flattenedMenus;
  }, [menuList]);

  const isLoading = useMemo(() => !flatMenuList.length, [flatMenuList]);

  useEffect(() => {
    getMenuLists();
  }, [getMenuLists]);

  useEffect(() => {
    getActiveMenu();
  }, [getActiveMenu]);

  return { menuList, menuMap, flatMenuList, isLoading };
};
